export type GstCategory = 'taxable' | 'exempt' | 'zero_rated';

/** Default only — live value comes from company GST settings / POS tax-settings. */
export const DEFAULT_GST_RATE = 5;
export const DEFAULT_GST_BUYER_THRESHOLD = 50000;

/** @deprecated use DEFAULT_GST_BUYER_THRESHOLD or taxSettings.buyer_threshold */
export const GST_BUYER_THRESHOLD = DEFAULT_GST_BUYER_THRESHOLD;

export type GstLine = {
  gst_category: GstCategory;
  gst_rate: number;
  taxable_value: number;
  exempt_value: number;
  gst_amount: number;
  gross: number;
};

function money(n: number): number {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

export function normalizeGstCategory(raw?: string | null): GstCategory {
  const value = String(raw || 'taxable').trim().toLowerCase();
  if (value === 'exempt') return 'exempt';
  if (value === 'zero_rated' || value === 'zero' || value === 'zero-rated' || value === 'export') {
    return 'zero_rated';
  }
  return 'taxable';
}

/** Matches backend app.core.gst.line_gst */
export function computeLineGst(
  chargedTotal: number,
  gstCategory?: string | null,
  inclusive = true,
  rate = 5,
): GstLine {
  const charged = money(chargedTotal);
  const category = normalizeGstCategory(gstCategory);
  const pct = money(rate < 0 ? 0 : rate);
  if (category !== 'taxable' || pct === 0 || charged === 0) {
    return {
      gst_category: category,
      gst_rate: category === 'taxable' ? pct : 0,
      taxable_value: category === 'taxable' ? charged : 0,
      exempt_value: category !== 'taxable' ? charged : 0,
      gst_amount: 0,
      gross: charged,
    };
  }
  if (inclusive) {
    const taxable = money((charged * 100) / (100 + pct));
    const gst_amount = money(charged - taxable);
    return {
      gst_category: category,
      gst_rate: pct,
      taxable_value: taxable,
      exempt_value: 0,
      gst_amount,
      gross: charged,
    };
  }
  const gst_amount = money((charged * pct) / 100);
  return {
    gst_category: category,
    gst_rate: pct,
    taxable_value: charged,
    exempt_value: 0,
    gst_amount,
    gross: money(charged + gst_amount),
  };
}

export function gstCartLineCaption(line: GstLine, inclusive = true): string {
  if (line.gst_category === 'exempt') {
    return 'Exempt — no GST';
  }
  if (line.gst_category === 'zero_rated') {
    return 'Zero-rated — no GST';
  }
  const amt = line.gst_amount.toFixed(2);
  if (inclusive) {
    return `GST ${line.gst_rate}% · Nu. ${amt} (included in price)`;
  }
  return `GST ${line.gst_rate}% · Nu. ${amt} (added)`;
}

export function gstReceiptLineTag(category?: string | null, gstAmount?: number | null): string {
  const cat = normalizeGstCategory(category);
  const amt = (Number(gstAmount) || 0).toFixed(2);
  if (cat === 'exempt') return 'Exempt — no GST';
  if (cat === 'zero_rated') return 'Zero-rated — no GST';
  if ((Number(gstAmount) || 0) <= 0) return 'Taxable';
  return `Taxable · GST Nu. ${amt}`;
}
